import { ChangeEvent, useEffect } from 'react'
import { Typography } from '@mui/material'
import { motion } from 'framer-motion'
import isEmpty from 'lodash/isEmpty'
import { useDispatch, useSelector } from 'react-redux'
import { getFolders } from '@admin/templates/folders/folders.actions'
import { Select } from '@components/formFields/select'
import { Input } from '@components/v2/input'
import { BasicInfo, CustomEvent, Options } from '../types'
import { AddFolderButton } from './addFolderButton'
import { t } from '@lingui/core/macro'

interface Props {
  basicInfo: BasicInfo
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void
  handleChangeCustom: ({ key, value }: CustomEvent) => void
}

/**
 * Component controlling the inputs for the *Basic Info* section of a template.
 * @param {Props} props **React props**
 * @param {BasicInfo} basicInfo - Values for name, description, and folder
 * @param {(e: ChangeEvent<HTMLInputElement>) => void} handleChange - The onChange handler for text fields
 * @param {({ key, value }: CustomEvent) => void} handleChangeCustom - The onChange handler for autocomplete
 */
export const BasicInfoSection = ({ basicInfo, handleChange, handleChangeCustom }: Props) => {
  const { name, description, folder_id } = basicInfo
  const dispatch = useDispatch()
  const folders = useSelector((state: any) => state.Folders.menuFolders)
  const animate = { transition: { delay: 0.1, duration: 0.2 } }

  useEffect(() => {
    dispatch(getFolders())
  }, [])

  return (
    <>
      <Typography variant="h3" gutterBottom>
        {t`Basic info`}
      </Typography>
      <motion.div animate={animate} initial={false}>
        <Input
          label={t`Name`}
          placeholder={t`Enter template name`}
          name="name"
          id="templateName"
          value={name || ''}
          onChange={handleChange}
          message="We recommend a name that has all important identifiable details."
          required
        />
      </motion.div>
      <motion.div animate={animate} initial={false}>
        <Input
          label={t`Description`}
          placeholder={t`Describe the use-cases for this template.`}
          name="description"
          id="templateDescription"
          value={description || ''}
          onChange={handleChange}
          type="textarea"
        />
      </motion.div>
      <motion.div
        animate={animate}
        initial={false}
        style={{ display: 'flex', alignItems: 'center', gap: '15px' }}
      >
        <Select
          name="Choose a Folder"
          popoverId="select-folder"
          data={folders.map(f => ({ id: f.value, name: f.label }))}
          selectedItems={[folder_id]}
          onSelectCallback={value => {
            handleChangeCustom({ key: Options.FOLDER_ID, value })
          }}
          isDisabled={isEmpty(folders)}
          multi={false}
        >
          <Select.VirtualList>
            {(index, template) => {
              return (
                <Select.Option key={template.id} id={template.id} index={index}>
                  {template.name}
                </Select.Option>
              )
            }}
          </Select.VirtualList>
        </Select>
        <AddFolderButton />
      </motion.div>
    </>
  )
}
